import axios from '@fs/zion-axios'
import Cache from '@fs/zion-cache'
import tryDemoFemaleOrdinancesReadyReservations from '../fixtures/tryDemo/try-demo-ordinances-ready-female-reservations.json'
import tryDemoMaleOrdinancesReadyReservations from '../fixtures/tryDemo/try-demo-ordinances-ready-male-reservations.json'
import personReservationTemplate from '../fixtures/tryDemo/try-demo-add-name-person-reservation-template.json'
import { mungeTreeData, personDemunge } from './mungers'
import { isValidPid } from './helpers'

export const tryDemoSessionCache = new Cache({
  storeName: 'ftte_tryDemo',
  type: 'session',
})
export const lcrCacheMemberKey = 'lcrDemo_Member'
const cacheFamilyMembersReservations = 'tryDemo_familyMemberReservations'
const cacheOrdReadyReservations = 'tryDemo_ordinancesReadyReservations'
const cacheTreeData = 'tryDemo_treeData'
const cacheLcrCalling = 'tryDemo_lcrCalling'

export async function getTryDemoLcrCalling() {
  const tryDemoLcrCalling = await tryDemoSessionCache.getItem(cacheLcrCalling)
  return tryDemoLcrCalling ?? 'bishop' // If missing, fall back on the Bishop role.
}

export function setTryDemoLcrCalling(role) {
  return tryDemoSessionCache.setItem(cacheLcrCalling, role)
}

export function tryDemoGetTreeData(memberDataUrl) {
  return axios.get(memberDataUrl).then(async (treeData) => {
    const tryDemoTreeData = await getCacheTreeData()
    const treeDataWithTryDemoTreeData = {
      ...treeData,
      data: {
        ...treeData.data,
        ...tryDemoTreeData,
      },
    }
    return mungeTreeData(treeDataWithTryDemoTreeData)
  })
}

export function tryDemoGetReservedNames() {
  const getCachedDemoData = async (resolve) => {
    const sessionCacheFamilyMembersReserved = await tryDemoSessionCache.getItem(cacheFamilyMembersReservations)
    const familyMembersReserved = sessionCacheFamilyMembersReserved || []
    const sessionCacheOrdinancesReadyReservations = await tryDemoSessionCache.getItem(cacheOrdReadyReservations)
    sessionCacheOrdinancesReadyReservations?.sort((a, b) => a.id.localeCompare(b.id))
    const ordinancesReadyReservations = sessionCacheOrdinancesReadyReservations || []

    return resolve([...ordinancesReadyReservations, ...familyMembersReserved])
  }
  return {
    cachePromise: new Promise(getCachedDemoData),
    serverPromise: new Promise(getCachedDemoData),
  }
}

export async function tryDemoRequestOrdinancesReadyNames() {
  const cacheOrdReadyToken = 'tryDemo_ordinancesReadyToken'
  await tryDemoSessionCache.setItem(cacheOrdReadyToken, cacheOrdReadyToken)
  return Promise.resolve(cacheOrdReadyToken)
}

export async function tryDemoPollOrdinancesReadyNames(memberPersonSex, personsOnReservationList, t) {
  const tryDemoOrdinancesReadyReservations =
    memberPersonSex === 'MALE' ? tryDemoMaleOrdinancesReadyReservations : tryDemoFemaleOrdinancesReadyReservations
  await tryDemoSessionCache.setItem(
    cacheOrdReadyReservations,
    translateNames(
      tryDemoOrdinancesReadyReservations
        .filter((person, index) => index < 4 - personsOnReservationList.length)
        .map((person) => {
          const personOnReservationList = personsOnReservationList.find((p) => p.id === person.id)
          if (personOnReservationList) return personOnReservationList
          return person
        }),
      t
    )
  )
  return Promise.resolve()
}

function translateNames(reservations, t) {
  return reservations.map((reservation) => {
    return {
      ...reservation,
      nameForms: reservation.nameForms.map((nameForm) => {
        return {
          ...nameForm,
          fullText: t(`common.close-relation.${nameForm.fullText.toLowerCase()}`, nameForm.fullText),
          parts: nameForm.parts.map((part) => {
            return {
              ...part,
              value: t(`common.close-relation.${part.value.toLowerCase()}`, part.value),
            }
          }),
        }
      }),
    }
  })
}

async function getTryDemoReservations() {
  const familyMembersReserved = structuredClone(await tryDemoSessionCache.getItem(cacheFamilyMembersReservations)) || []
  const ordinancesReadyReservations =
    structuredClone(await tryDemoSessionCache.getItem(cacheOrdReadyReservations)) || []
  return { familyMembersReserved, ordinancesReadyReservations }
}

async function getCacheTreeData() {
  return (
    structuredClone(await tryDemoSessionCache.getItem(cacheTreeData)) || {
      couplesRelationships: [],
      parentChildRelationshipsAsParent: [],
      parentsPairings: [],
      relatives: {},
    }
  )
}

export async function tryDemoShareWithTemple(pids) {
  const allReservations = await getTryDemoReservations()
  pids.forEach((pid) => {
    const personToUpdate = [
      ...allReservations.familyMembersReserved,
      ...allReservations.ordinancesReadyReservations,
    ].find((person) => person.id === pid)
    personToUpdate.reservations = personToUpdate.reservations.map((reservation) => ({
      ...reservation,
      actions: {
        ...reservation.actions,
        unShareable: true,
        templeShareable: false,
      },
    }))
  })
  await tryDemoSessionCache.setItem(cacheFamilyMembersReservations, allReservations.familyMembersReserved)
  await tryDemoSessionCache.setItem(cacheOrdReadyReservations, allReservations.ordinancesReadyReservations)

  return Promise.resolve()
}

export async function tryDemoUnshareWithTemple(pids) {
  const allReservations = await getTryDemoReservations()
  pids.forEach((pid) => {
    const personToUpdate = [
      ...allReservations.familyMembersReserved,
      ...allReservations.ordinancesReadyReservations,
    ].find((person) => person.id === pid)
    personToUpdate.reservations = personToUpdate.reservations.map((reservation) => ({
      ...reservation,
      actions: {
        ...reservation.actions,
        unShareable: false,
        templeShareable: true,
      },
    }))
  })
  await tryDemoSessionCache.setItem(cacheFamilyMembersReservations, allReservations.familyMembersReserved)
  await tryDemoSessionCache.setItem(cacheOrdReadyReservations, allReservations.ordinancesReadyReservations)

  return Promise.resolve()
}

export async function tryDemoShareWithIndividual(pids) {
  const allReservations = await getTryDemoReservations()
  pids.forEach((pid) => {
    const personToUpdate = [
      ...allReservations.familyMembersReserved,
      ...allReservations.ordinancesReadyReservations,
    ].find((person) => person.id === pid)
    personToUpdate.reservations = personToUpdate.reservations.map((reservation) => ({
      ...reservation,
      actions: Object.keys(reservation.actions).reduce((acc, key) => ({ ...acc, [key]: false }), {}),
    }))
    personToUpdate.sharedToIndividualInfo = {}
  })
  await tryDemoSessionCache.setItem(cacheFamilyMembersReservations, allReservations.familyMembersReserved)
  await tryDemoSessionCache.setItem(cacheOrdReadyReservations, allReservations.ordinancesReadyReservations)

  return Promise.resolve()
}

export function tryDemoGetTempleForId() {
  return Promise.resolve('100231231231231231')
}

export function tryDemoGetPersonsPortraits() {
  return Promise.resolve({})
}

export function tryDemoSetWorkingPersonData(
  metadata,
  persons = [],
  coupleRelationships = [],
  parentChildRelationships = []
) {
  const workingPersonContainer = {
    metadata,
    persons: structuredClone(persons),
    coupleRelationships: structuredClone(coupleRelationships),
    parentChildRelationships: structuredClone(parentChildRelationships),
  }

  workingPersonContainer.persons?.forEach((person, index, array) => {
    array[index] = personDemunge(person)
    delete array[index].baptism
    delete array[index].confirmation
  })
  return Promise.resolve()
}

export async function tryDemoEditPerson(person) {
  const tryDemoTreeData = await getCacheTreeData()
  const relativeToEdit = tryDemoTreeData.relatives[person.id]
  relativeToEdit.lifespanBegin = person.lifespanBegin
  relativeToEdit.lifespanEnd = person.lifespanEnd
  relativeToEdit.living = !person.lifespanEnd
  relativeToEdit.nameForms = person.nameForms
  await tryDemoSessionCache.setItem(cacheTreeData, tryDemoTreeData)
}

export async function tryDemoReserveName(workingPerson, metadata) {
  const allReservations = await getTryDemoReservations()
  if (!allReservations.familyMembersReserved.find((person) => person.id === workingPerson.id)) {
    const tryDemoTreeData = await getCacheTreeData()
    tryDemoTreeData.relatives[workingPerson.id].baptism.status = 'RESERVED'
    tryDemoTreeData.relatives[workingPerson.id].confirmation.status = 'RESERVED'
    await tryDemoSessionCache.setItem(cacheTreeData, tryDemoTreeData)
    const personReservation = getPersonReservation(workingPerson, metadata)
    allReservations.familyMembersReserved.push(personReservation)
    await tryDemoSessionCache.setItem(cacheFamilyMembersReservations, allReservations.familyMembersReserved)
  }
  return Promise.resolve()
}

export async function tryDemoCreatePerson(metadata, persons) {
  const tryDemoTreeData = await getCacheTreeData()
  const numberOfRelatives = Object.keys(tryDemoTreeData.relatives).length
  const createPersonPidNumber = numberOfRelatives + 5
  const createdPersonId = `ABCD-${createPersonPidNumber.toString().padStart(3, '0')}`
  const createdPersonParentsPairings = []
  const workingPerson = persons[0]
  switch (metadata.relation) {
    case 'child': {
      let otherParentId = isValidPid(metadata.gapParents.spouseId, true) ? metadata.gapParents.spouseId : undefined
      // if there is a new parent being added, add new parent to treeData
      if (!otherParentId && persons.length > 1) {
        const otherParent = persons[1]
        const otherParentIdNumber = createPersonPidNumber + 1
        otherParentId = `ABCD-${otherParentIdNumber.toString().padStart(3, '0')}`
        // add new parent as user relative
        tryDemoTreeData.relatives[otherParentId] = createRelativeDataObject(
          otherParentId,
          otherParent.sex,
          otherParent.living,
          otherParent.nameForms
        )
        // add new couple relationship for user with new parent
        tryDemoTreeData.couplesRelationships.push({
          selfId: metadata.memberPerson.id,
          otherId: otherParentId,
          facts: [
            {
              date: {
                isStandard: true,
              },
              place: {
                isStandard: true,
              },
              type: 'http://gedcomx.org/Marriage',
            },
          ],
        })
      }
      // add parent child relationship for user with child
      tryDemoTreeData.parentChildRelationshipsAsParent.push({
        selfId: metadata.memberPerson.id,
        otherParentId,
        childId: createdPersonId,
      })
      break
    }
    case 'grandparent': {
      let parent = metadata.connectingParent.id && tryDemoTreeData.relatives[metadata.connectingParent.id]
      if (!parent && metadata.gapParents.fatherId) {
        parent = tryDemoTreeData.relatives[metadata.gapParents.fatherId]
      } else if (!parent && metadata.gapParents.motherId) {
        parent = tryDemoTreeData.relatives[metadata.gapParents.motherId]
      }
      if (!parent) {
        const parentPerson = persons[1]
        const parentIdNumber = createPersonPidNumber + 1
        const parentId = `ABCD-${parentIdNumber.toString().padStart(3, '0')}`
        // add new parent as user relative
        tryDemoTreeData.relatives[parentId] = createRelativeDataObject(
          parentId,
          parentPerson.sex,
          parentPerson.living,
          parentPerson.nameForms
        )
        // add new parent in user parent pairing
        tryDemoTreeData.parentsPairings.push({
          personId1: parentPerson.sex === 'MALE' ? parentId : undefined,
          personId2: parentPerson.sex === 'FEMALE' ? parentId : undefined,
        })
        parent = tryDemoTreeData.relatives[parentId]
      }
      const workingPersonIdParts = workingPerson.id.split('-')
      let grandParentSpouseId = null
      if (workingPersonIdParts.length === 3 && workingPersonIdParts[2] === 'spouse') {
        grandParentSpouseId = workingPersonIdParts.slice(0, 2).join('-')
      }
      const workingPersonSex = workingPerson.sex
      let grandparentId1 = workingPersonSex === 'MALE' ? createdPersonId : grandParentSpouseId
      grandparentId1 = isValidPid(grandparentId1, true) ? grandparentId1 : undefined
      let grandparentId2 = workingPersonSex === 'MALE' ? grandParentSpouseId : createdPersonId
      grandparentId2 = isValidPid(grandparentId2, true) ? grandparentId2 : undefined
      if (grandparentId1 && grandparentId2) {
        parent.parentsPairings = removePersonParentsPairings(grandparentId1, grandparentId2, parent.parentsPairings)
      }
      // add parent pairing for connecting parent
      parent.parentsPairings.push({
        personId1: grandparentId1,
        personId2: grandparentId2,
      })
      break
    }
    case 'parent': {
      const otherParent = metadata.connectingParent
      const workingPersonSex = workingPerson.sex
      let parentId1 = workingPersonSex === 'MALE' ? createdPersonId : otherParent.id
      parentId1 = isValidPid(parentId1, true) ? parentId1 : undefined
      let parentId2 = workingPersonSex === 'MALE' ? otherParent.id : createdPersonId
      parentId2 = isValidPid(parentId2, true) ? parentId2 : undefined
      // only update parent pairing if no sibling exists with the current parent pairing
      const parentPairingExistsForSibling = Object.values(tryDemoTreeData.relatives).some((person) =>
        person.parentsPairings.some(
          (parentPairing) => parentPairing.personId1 === parentId1 || parentPairing.personId2 === parentId2
        )
      )
      if (parentId1 && parentId2 && !parentPairingExistsForSibling) {
        tryDemoTreeData.parentsPairings = removePersonParentsPairings(
          parentId1,
          parentId2,
          tryDemoTreeData.parentsPairings
        )
      }
      // add parent pairing for user
      tryDemoTreeData.parentsPairings.push({
        personId1: parentId1,
        personId2: parentId2,
      })
      break
    }
    case 'sibling': {
      let fatherId = metadata.gapParents.fatherId !== 'UNKNOWN' ? metadata.gapParents.fatherId : undefined
      let motherId = metadata.gapParents.motherId !== 'UNKNOWN' ? metadata.gapParents.motherId : undefined
      let father = fatherId && tryDemoTreeData.relatives[fatherId]
      let mother = motherId && tryDemoTreeData.relatives[motherId]
      // if there is a father being added, add father to treeData
      if (fatherId === 'ADD FATHER' && persons.length > 1) {
        const fatherPerson = persons.find((person) => person.id === fatherId)
        const fatherPersonIdNumber = createPersonPidNumber + 1
        fatherId = `ABCD-${fatherPersonIdNumber.toString().padStart(3, '0')}`
        // add new father as user relative
        tryDemoTreeData.relatives[fatherId] = createRelativeDataObject(
          fatherId,
          fatherPerson.sex,
          fatherPerson.living,
          fatherPerson.nameForms
        )
        father = tryDemoTreeData.relatives[fatherId]
      }
      // if there is a mother being added, add mother to treeData
      if (motherId === 'ADD MOTHER' && persons.length > 1) {
        const motherPerson = persons.find((person) => person.id === motherId)
        const motherPersonIdNumber = createPersonPidNumber + persons.length - 1
        motherId = `ABCD-${motherPersonIdNumber.toString().padStart(3, '0')}`
        // add new mother as user relative
        tryDemoTreeData.relatives[motherId] = createRelativeDataObject(
          motherId,
          motherPerson.sex,
          motherPerson.living,
          motherPerson.nameForms
        )
        mother = tryDemoTreeData.relatives[motherId]
      }
      // add parent child relationships as parent for both parents
      father?.parentChildRelationshipsAsParent.push({
        selfId: fatherId,
        otherParentId: motherId,
        childId: createdPersonId,
      })
      mother?.parentChildRelationshipsAsParent.push({
        selfId: motherId,
        otherParentId: fatherId,
        childId: createdPersonId,
      })
      // add parent pairing for sibling
      createdPersonParentsPairings.push({
        personId1: fatherId,
        personId2: motherId,
      })
      // if new parent pairing doesn't exist for user between, create it
      if (
        !tryDemoTreeData.parentsPairings.find(
          (parentPairing) => parentPairing.personId1 === fatherId && parentPairing.personId2 === motherId
        )
      ) {
        tryDemoTreeData.parentsPairings.push({
          personId1: fatherId,
          personId2: motherId,
        })
      }
      break
    }
    case 'spouse': {
      // add couple relationship for user
      tryDemoTreeData.couplesRelationships.push({
        selfId: metadata.memberPerson.id,
        otherId: createdPersonId,
        facts: [
          {
            type: 'http://gedcomx.org/Marriage',
          },
        ],
      })
      break
    }
    default:
      break
  }
  // add new person as user relative
  tryDemoTreeData.relatives[createdPersonId] = createRelativeDataObject(
    createdPersonId,
    workingPerson.sex,
    workingPerson.living,
    workingPerson.nameForms,
    createdPersonParentsPairings
  )
  await tryDemoSessionCache.setItem(cacheTreeData, tryDemoTreeData)
  return Promise.resolve(createdPersonId)
}

function removePersonParentsPairings(parentId1, parentId2, parentsPairings) {
  return parentsPairings.filter((parentPairing) => {
    return (
      !(parentPairing.personId1 !== parentId1 && parentPairing.personId2 === parentId2) &&
      !(parentPairing.personId1 === parentId1 && parentPairing.personId2 !== parentId2)
    )
  })
}

function createRelativeDataObject(personId, sex, living, nameForms, parentsPairings = []) {
  return {
    id: personId,
    sex,
    living: !!living,
    nameForms,
    baptism: {
      status: 'READY',
      statusReasons: [],
    },
    confirmation: {
      status: 'READY',
      statusReasons: [],
    },
    couplesRelationships: [],
    parentChildRelationshipsAsParent: [],
    parentsPairings,
    relatives: {},
  }
}

function createPersonLifespan(date, place, type) {
  return {
    ...(date && {
      date: {
        original: date.standard.originalText,
        formal: date.standard.formalText,
        normalized: [{ value: date.standard.standardizedText }],
      },
    }),
    ...(place && {
      place: {
        original: place.standard.primaryText,
        id: place.standard.countryId,
        names: [{ value: place.standard.standardizedText }],
      },
    }),
    type,
  }
}

function getPersonReservation(workingPerson, metadata) {
  const personReservation = structuredClone(personReservationTemplate)
  personReservation.id = workingPerson.id
  personReservation.sex = workingPerson.sex
  personReservation.nameForms = workingPerson.nameForms
  personReservation.lifespanBegin = createPersonLifespan(
    workingPerson.birthDate,
    workingPerson.birthPlace,
    'http://gedcomx.org/Birth'
  )
  personReservation.lifespanEnd = createPersonLifespan(
    workingPerson.deathDate,
    workingPerson.deathPlace,
    'http://gedcomx.org/Death'
  )
  setRelationshipDetails(personReservation, metadata)
  return personReservation
}

function setRelationshipDetails(personReservation, metadata) {
  switch (metadata.relationSpecific) {
    case 'son':
      personReservation.pathSummary = 'M.m'
      break
    case 'daughter':
      personReservation.pathSummary = 'M.f'
      break
    case 'husband':
      personReservation.pathSummary = 'husband'
      personReservation.isSpouse = true
      break
    case 'wife':
      personReservation.pathSummary = 'wife'
      personReservation.isSpouse = true
      break
    case 'father':
      personReservation.pathSummary = 'Mm.'
      break
    case 'mother':
      personReservation.pathSummary = 'Mf.'
      break
    case 'brother':
      personReservation.pathSummary = 'Mf.m'
      break
    case 'sister':
      personReservation.pathSummary = 'Mf.f'
      break
    case 'grandfather':
      if (metadata.connectingParent.id?.includes('father') || metadata.gapParents.fatherId) {
        personReservation.pathSummary = 'Mmm.'
      }
      personReservation.pathSummary = 'Mfm.'
      break
    case 'grandmother':
      if (metadata.connectingParent.id?.includes('father') || metadata.gapParents.fatherId) {
        personReservation.pathSummary = 'Mmf.'
      }
      personReservation.pathSummary = 'Mff.'
      break
    default:
      break
  }
}
